import {
  Mic,
  Radio,
  Users,
  Heart,
  ArrowRight,
  Eye,
  Volume2,
  Award,
  Globe,
} from "lucide-react";

import { ACCENT, ACCENT_LIGHT } from "../constants/theme";
import Btn from "../components/Button";
import Card from "../components/Card";
import SectionLabel from "../components/SectionLabel";

export default function About({ setPage }) {
  return (
    <div>
      {/* Hero */}
      <section
        style={{
          background: ACCENT_LIGHT,
          padding: "80px 24px 64px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 780, margin: "0 auto" }}>
          <SectionLabel>About Nexus Studio</SectionLabel>
          <h1
            style={{
              fontSize: "clamp(32px, 5vw, 56px)",
              fontWeight: 900,
              color: "#0f172a",
              letterSpacing: "-1.5px",
              marginBottom: 18,
            }}
          >
            Vision Beyond Inclusion.
          </h1>
          <p style={{ color: "#475569", fontSize: 17, lineHeight: 1.75 }}>
            Nexus Studio is a pioneering creative platform and inclusive
            workplace where disabled and non-disabled creators come together to
            collaborate, learn, and grow on equal ground.
          </p>
        </div>
      </section>

      {/* Main About */}
      <section style={{ background: "#fff", padding: "80px 24px" }}>
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: 56,
            alignItems: "center",
          }}
        >
          <div>
            <SectionLabel>Who We Are</SectionLabel>
            <h2
              style={{
                fontSize: 34,
                fontWeight: 800,
                color: "#0f172a",
                letterSpacing: "-0.8px",
                marginBottom: 20,
              }}
            >
              Creativity Knows No Boundaries
            </h2>

            <p
              style={{
                color: "#475569",
                fontSize: 15.5,
                lineHeight: 1.8,
                marginBottom: 16,
              }}
            >
              At Nexus Studio, we believe that true creativity has no limits. We
              are dedicated to bridging the gap between individuals with
              disabilities and non-disabled creators by building a space where
              talent, imagination, and opportunity meet.
            </p>

            <p
              style={{
                color: "#475569",
                fontSize: 15.5,
                lineHeight: 1.8,
              }}
            >
              We are more than a studio. We are a launchpad for potential —
              empowering creators with a stage to showcase their unique talents,
              along with certified courses and professional training to help
              them excel in their creative journey.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
            }}
          >
            {[
              { icon: <Users size={22} color={ACCENT} />, label: "Inclusive Workplace" },
              { icon: <Award size={22} color={ACCENT} />, label: "Certified Training" },
              { icon: <Mic size={22} color={ACCENT} />, label: "Audio Production" },
              { icon: <Globe size={22} color={ACCENT} />, label: "Equal Opportunity" },
            ].map(({ icon, label }) => (
              <div
                key={label}
                style={{
                  background: ACCENT_LIGHT,
                  borderRadius: 14,
                  padding: "28px 20px",
                  textAlign: "center",
                }}
              >
                <div
                  style={{
                    width: 44,
                    height: 44,
                    background: "#fff",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    margin: "0 auto 12px",
                  }}
                >
                  {icon}
                </div>
                <p style={{ fontWeight: 700, fontSize: 14, color: "#0f172a" }}>
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section style={{ background: "#f8fafc", padding: "80px 24px" }}>
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 28,
          }}
        >
          {[
            {
              label: "Our Mission",
              icon: <Heart size={26} color={ACCENT} />,
              text:
                "To foster an environment where diverse talents unite, collaborate, and thrive on equal ground through creative projects, professional training, and meaningful opportunities.",
            },
            {
              label: "Our Vision",
              icon: <Eye size={26} color={ACCENT} />,
              text:
                "To build a creative world where inclusion is not an exception, but the foundation — where every creator has the stage, skills, and confidence to be heard.",
            },
          ].map(({ label, icon, text }) => (
            <Card key={label}>
              <div
                style={{
                  width: 54,
                  height: 54,
                  background: ACCENT_LIGHT,
                  borderRadius: 14,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 20,
                }}
              >
                {icon}
              </div>
              <h3
                style={{
                  fontWeight: 800,
                  fontSize: 22,
                  color: "#0f172a",
                  marginBottom: 14,
                }}
              >
                {label}
              </h3>
              <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.75 }}>
                {text}
              </p>
            </Card>
          ))}
        </div>
      </section>

      {/* What We Do */}
      <section style={{ background: "#fff", padding: "80px 24px" }}>
        <div
          style={{
            maxWidth: 900,
            margin: "0 auto",
            textAlign: "center",
            marginBottom: 52,
          }}
        >
          <SectionLabel>What We Do</SectionLabel>
          <h2
            style={{
              fontSize: "clamp(26px, 4vw, 40px)",
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.8px",
            }}
          >
            Next-Gen Audio Entertainment
          </h2>
          <p
            style={{
              color: "#64748b",
              fontSize: 16,
              lineHeight: 1.75,
              maxWidth: 680,
              margin: "14px auto 0",
            }}
          >
            Our core passion lies in reshaping the world of audio entertainment.
            We create high-quality sound experiences that bring stories, voices,
            and ideas to life.
          </p>
        </div>

        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
            gap: 28,
          }}
        >
          <Card>
            <Volume2 size={28} color={ACCENT} style={{ marginBottom: 16 }} />
            <h3
              style={{
                fontWeight: 700,
                fontSize: 19,
                color: "#0f172a",
                marginBottom: 12,
              }}
            >
              Immersive Audio Stories
            </h3>
            <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.7 }}>
              Rich narrative experiences driven by compelling characters and
              cutting-edge sound design that place the listener right in the
              center of the action.
            </p>
          </Card>

          <Card>
            <Radio size={28} color={ACCENT} style={{ marginBottom: 16 }} />
            <h3
              style={{
                fontWeight: 700,
                fontSize: 19,
                color: "#0f172a",
                marginBottom: 12,
              }}
            >
              Engaging Podcasts
            </h3>
            <p style={{ color: "#64748b", fontSize: 15, lineHeight: 1.7 }}>
              Thought-provoking and entertaining audio content that amplifies
              diverse voices, fresh perspectives, and meaningful conversations.
            </p>
          </Card>
        </div>
      </section>

      {/* Why Nexus */}
      <section style={{ background: "#f8fafc", padding: "80px 24px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <SectionLabel>Why Nexus?</SectionLabel>
          <h2
            style={{
              fontSize: "clamp(26px, 4vw, 40px)",
              fontWeight: 800,
              color: "#0f172a",
              letterSpacing: "-0.8px",
              marginBottom: 18,
            }}
          >
            Changing Who Gets To Tell Stories
          </h2>
          <p
            style={{
              color: "#64748b",
              fontSize: 16,
              lineHeight: 1.8,
              maxWidth: 720,
              margin: "0 auto",
            }}
          >
            By pushing the boundaries of sound and inclusivity, Nexus Studio is
            not just changing how stories are told — we are changing who gets to
            tell them.
          </p>
        </div>
      </section>

      {/* Closing quote */}
      <section style={{ background: ACCENT_LIGHT, padding: "72px 24px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontSize: "clamp(18px, 3vw, 26px)",
              fontWeight: 700,
              color: "#0f172a",
              lineHeight: 1.6,
              fontStyle: "italic",
            }}
          >
            “When we look beyond mere inclusion, we discover a world of
            limitless, untapped vision.”
          </p>

          <div
            style={{
              width: 48,
              height: 3,
              background: ACCENT,
              borderRadius: 4,
              margin: "28px auto 32px",
            }}
          />

          <Btn onClick={() => setPage("Join Us")}>
            Be Part of the Vision <ArrowRight size={16} />
          </Btn>
        </div>
      </section>
    </div>
  );
}